// Spec 39. PostHog analytics + session replay, behind one typed service.
//
// The whole app talks to `analytics` (not posthog directly) so event names and
// payloads are checked at the callsite and stay consistent. The provider in
// main.tsx still gets the raw `posthog` client for the usePostHog / feature-
// flag hooks. Everything no-ops when no key is configured (tests, local dev
// without a key), so callsites never need to guard.

import posthog from "posthog-js";

// The project token is a public client key — safe to ship in the bundle. Env
// overrides let a build point at a different project or a reverse proxy.
const KEY =
	import.meta.env.VITE_PUBLIC_POSTHOG_KEY ??
	"phc_oUmiWiT2FKPPoCZJJhwheaZ95bsYgx3stAuqjUGV4VZ7";
const HOST =
	import.meta.env.VITE_PUBLIC_POSTHOG_HOST ?? "https://eu.i.posthog.com";

const enabled = !!KEY;

// Closed event catalog: adding a callsite means adding the event here, so the
// taxonomy can't drift. Keep names snake_case, properties flat.
export interface AnalyticsEvents {
	signin_attempted: {
		provider: "github" | "google" | "codespring" | "magic_link";
	};
	onboarding_started: Record<string, never>;
	onboarding_step_viewed: { widget: string };
	onboarding_step_submitted: { widget: string; action: string };
	onboarding_step_result: {
		widget: string;
		result: "succeeded" | "failed" | "cancelled";
	};
	onboarding_source_connected: { provider: "github" | "gmail" };
	onboarding_completed: Record<string, never>;
	pricing_viewed: { current_plan?: string };
	plan_cta_clicked: {
		plan: "free" | "lite" | "pro";
		kind: "subscribe" | "contact" | "current";
	};
	checkout_redirected: { plan: string };
	subscription_succeeded: { plan: string };
	billing_portal_opened: Record<string, never>;
}

interface IdentifyUser {
	id: string;
	email?: string | null;
	name?: string | null;
}

const GROUP_TYPE = "organization";

let started = false;

/** Initialise PostHog once, at app startup, before render. */
export function initAnalytics(): void {
	if (!enabled || started) return;
	started = true;
	posthog.init(KEY, {
		api_host: HOST,
		// Rolls up the current recommended defaults: history-change pageviews
		// (SPA-friendly), strict minimum recording duration, rageclick, etc.
		defaults: "2026-05-30",
		person_profiles: "identified_only",
		// Spec 39: GDPR consent gate. Nothing is captured — no pageviews, no
		// events, no session recording — until the user explicitly opts in via
		// the consent banner. EU Cloud + opt-out-by-default.
		opt_out_capturing_by_default: true,
		session_recording: {
			maskAllInputs: true,
			maskInputOptions: { password: true },
			// Opt-in text masking: add `data-ph-mask` to anything that renders
			// a raw secret (e.g. an API-key reveal).
			maskTextSelector: "[data-ph-mask]",
		},
	});
}

export const analytics = {
	/** The raw client, for <PostHogProvider> and the feature-flag hooks. */
	client: posthog,

	capture<K extends keyof AnalyticsEvents>(
		event: K,
		properties?: AnalyticsEvents[K],
	): void {
		if (!enabled) return;
		posthog.capture(event, properties);
	},

	/** Link subsequent events to a known user; set the org group if present. */
	identify(user: IdentifyUser, organizationId?: string | null): void {
		if (!enabled) return;
		posthog.identify(user.id, {
			email: user.email ?? undefined,
			name: user.name ?? undefined,
		});
		if (organizationId) posthog.group(GROUP_TYPE, organizationId);
	},

	/** Associate the active organization (workspace) as the current group. */
	group(organizationId: string): void {
		if (!enabled) return;
		posthog.group(GROUP_TYPE, organizationId);
	},

	/** Clear identity on logout so events don't bleed across users. */
	reset(): void {
		if (!enabled) return;
		posthog.reset();
	},

	// --- Consent (spec 39) ---

	/**
	 * Explicit consent state: "pending" (no decision yet — show the banner),
	 * "granted", or "denied". Returns "denied" when analytics is disabled so
	 * the banner never shows. Note: don't gate the banner on
	 * has_opted_in_capturing(), which can read true while still pending.
	 */
	consentStatus(): "pending" | "granted" | "denied" {
		if (!enabled) return "denied";
		return posthog.get_explicit_consent_status() as
			| "pending"
			| "granted"
			| "denied";
	},

	/** User accepted: start capturing (persisted by PostHog). */
	optIn(): void {
		if (!enabled) return;
		posthog.opt_in_capturing();
	},

	/** User declined: stay opted out (persisted by PostHog). */
	optOut(): void {
		if (!enabled) return;
		posthog.opt_out_capturing();
	},
};
